/** How long after mount the chart may go with zero tile requests before the
 *  timeout note ("The chart isn't requesting any tiles.") appears with Retry.
 *  The deadline starts with the chart, even if MapLibre never asks for a tile. */
export const CHART_LOADING_GRACE_MS = 60_000;

export type ChartBackstopKind = 'zero' | 'stalled' | null;

/** Which 60s backstop case applies at the deadline. 'zero': no tiles were
 *  requested — the pipeline never started (#28). 'stalled': tiles were
 *  requested but the loading pill never cleared (#144). null when the
 *  chart became ready in time — a finished load is never a timeout. */
export function backstopCase(
  requested: number,
  chartsState: 'loading' | 'ready'
): ChartBackstopKind {
  if (chartsState !== 'loading') return null;
  return requested === 0 ? 'zero' : 'stalled';
}

/** First-paint loading pill text. The settled count stays hidden until the
 *  first tile request registers — a count at first paint reads as there being
 *  nothing to load (#98), so before any request the pill is text-only. */
export function loadingPillText(requested: number, settled: number): string {
  if (requested === 0) return 'Loading charts…';
  return `Loading charts… ${settled} ${settled === 1 ? 'tile' : 'tiles'}`;
}

export function dismissPillWhenTilesSettle(
  map: {
    areTilesLoaded(): boolean;
    once(event: 'idle' | 'dataloading', fn: () => void): void;
  },
  requested: number,
  onReady: () => void
): void {
  if (requested > 0) {
    if (map.areTilesLoaded()) onReady();
    else map.once('idle', onReady);
    return;
  }

  map.once('dataloading', () => map.once('idle', onReady));
}
